import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { AxiosApi } from "../../helpers/AxiosApi";
import initialStates from "./state";

// Get notification List
export const getNotificationList = createAsyncThunk(
  "getNotificationList",
  async (
    { search, pageLimit, pageCount, sort_by, sort_direction },
    { rejectWithValue }
  ) => {
    try {
      let queryParams = [];

      if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
      if (pageCount) queryParams.push(`page=${pageCount}`);
      if (pageLimit) queryParams.push(`limit=${pageLimit}`);
      if (sort_by) queryParams.push(`sortBy=${sort_by}`);
      if (sort_direction) queryParams.push(`sortOrder=${sort_direction}`);

      const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

      const response = await AxiosApi.get(
        `/admin/get-notifications${queryString}`
      );
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Something went wrong"
      );
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

// Create notification (without sending)
export const createNotification = createAsyncThunk(
  "createNotification",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.post(
        `/admin/create-notification`,
        data
      );
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Edit notification
export const editNotification = createAsyncThunk(
  "editNotification",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.put(`/admin/update-notificationdata`, data);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Send and re-send notification to all users
export const sendAgainNotification = createAsyncThunk(
  "sendAgainNotification",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.put(`/admin/send-notification-allUser`, data);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  "deleteNotification",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.delete(`/admin/delete-notification/${id}`);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

const notificationSlice = createSlice({
  name: "notification",
  initialState: initialStates,

  extraReducers: (builder) => {
    builder
      // get notification list
      .addCase(getNotificationList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNotificationList.fulfilled, (state, action) => {
        state.loading = false;
        state.getNotificationListStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getNotificationList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      //create notification
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.createNotificationStatus.data = action.payload;
        state.error = false;
      })
      .addCase(createNotification.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      //edit notification
      .addCase(editNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(editNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.editNotificationStatus.data = action.payload;
        state.error = false;
      })
      .addCase(editNotification.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      //send again notification
      .addCase(sendAgainNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendAgainNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.sendAgainNotificationStatus.data = action.payload;
        state.error = false;
      })
      .addCase(sendAgainNotification.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      //delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteNotificationStatus.data = action.payload;
        state.error = false;
      })
      .addCase(deleteNotification.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
  },
});

export const { setNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
